import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {X} from "lucide-react";
import {cn} from "../../utils/cn";

const sizeMap = {
  sm: "w-full sm:max-w-[420px]",
  md: "w-full sm:max-w-[560px]",
  lg: "w-full sm:max-w-[720px]",
  xl: "w-full sm:max-w-[920px]",
};

/**
 * Right-anchored slide-over panel. The workhorse for forms/edits.
 *
 * Composition:
 *   <SlideOver open={…} onOpenChange={…} title="…" subtitle="…" size="md">
 *     <SlideOver.Body>…</SlideOver.Body>
 *     <SlideOver.Footer>…</SlideOver.Footer>
 *   </SlideOver>
 */
function SlideOver({open, onOpenChange, title, subtitle, size = "md", children, className, onEscapeKeyDown}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 bg-ink-900/30 z-slideover",
            "data-[state=open]:animate-fade-in",
            "data-[state=closed]:animate-fade-out"
          )}
        />
        <Dialog.Content
          onEscapeKeyDown={onEscapeKeyDown}
          className={cn(
            "fixed right-0 top-0 bottom-0 z-slideover",
            "bg-white shadow-xl border-l border-ink-200",
            "flex flex-col focus:outline-none",
            "data-[state=open]:animate-slide-in-right",
            "data-[state=closed]:animate-slide-out-right",
            sizeMap[size],
            className
          )}
        >
          <div className="px-6 py-4 border-b border-ink-100 flex items-start justify-between gap-3 shrink-0">
            <div className="min-w-0">
              {title && (
                <Dialog.Title className="text-base font-semibold text-ink-900 truncate">{title}</Dialog.Title>
              )}
              {subtitle && (
                <Dialog.Description className="text-sm text-ink-500 mt-0.5 truncate">
                  {subtitle}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                className="inline-flex items-center justify-center h-8 w-8 rounded-md text-ink-500 hover:bg-ink-100 hover:text-ink-700 focus-visible:ring"
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

SlideOver.Body = function SlideOverBody({className, children, ...props}) {
  return (
    <div className={cn("flex-1 overflow-y-auto px-6 py-5", className)} {...props}>
      {children}
    </div>
  );
};

SlideOver.Footer = function SlideOverFooter({className, children, ...props}) {
  return (
    <div
      className={cn(
        "px-6 py-4 border-t border-ink-100 flex items-center justify-end gap-2 shrink-0",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

export default SlideOver;
